import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, Contact, Company, ContextEdge, Interaction } from '@nexus/shared/types';

interface ContextEntity {
  type: 'contact' | 'company';
  id: string;
  name: string;
  email?: string;
  sentiment?: string;
}

interface ContextState {
  contacts: Contact[];
  companies: Company[];
  edges: ContextEdge[];
  interactions: Interaction[];
  selectedEntity: ContextEntity | null;
  isLoading: boolean;
  error: string | null;
  fetchEntities: (q?: string) => Promise<void>;
  fetchContext: (entityType: string, entityId: string) => Promise<void>;
  createEdge: (data: Record<string, unknown>) => Promise<void>;
  removeEdge: (id: string) => Promise<void>;
  selectEntity: (entity: ContextEntity | null) => void;
  clearError: () => void;
}

export const useContextStore = create<ContextState>((set, get) => ({
  contacts: [],
  companies: [],
  edges: [],
  interactions: [],
  selectedEntity: null,
  isLoading: false,
  error: null,

  fetchEntities: async (q) => {
    set({ isLoading: true, error: null });
    try {
      const params = q ? { q, perPage: 50 } : { perPage: 50 };
      const [contactsRes, companiesRes] = await Promise.all([
        api.get<ApiResponse<Contact[]>>(`/api/contacts${toQueryString(params)}`),
        api.get<ApiResponse<Company[]>>(`/api/companies${toQueryString(params)}`),
      ]);
      set({
        contacts: contactsRes.data ?? [],
        companies: companiesRes.data ?? [],
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  fetchContext: async (entityType, entityId) => {
    try {
      const res = await api.get<ApiResponse<{ edges: ContextEdge[]; interactions: Interaction[] }>>(
        `/api/context/${entityType}/${entityId}`,
      );
      const data = res.data;
      set({
        edges: data?.edges ?? [],
        interactions: (data?.interactions ?? []).map(i => ({
          ...i,
          createdAt: fromUnix((i as unknown as Record<string, unknown>).createdAt as number),
        })) as Interaction[],
      });
    } catch { /* ignore */ }
  },

  createEdge: async (data) => {
    await api.post('/api/context/edges', data);
    const entity = get().selectedEntity;
    if (entity) get().fetchContext(entity.type, entity.id);
  },

  removeEdge: async (id) => {
    await api.del(`/api/context/edges/${id}`);
    set((s) => ({ edges: s.edges.filter((e) => e.id !== id) }));
  },

  selectEntity: (entity) => {
    set({ selectedEntity: entity, edges: [], interactions: [] });
    if (entity) get().fetchContext(entity.type, entity.id);
  },

  clearError: () => set({ error: null }),
}));
